/**
 * Break-recommendation modal for the Learner Readiness Score.
 *
 * Fires when the score drops below threshold AND all preconditions hold
 * (per spec §11.1). Visual chassis is consistent with ConfidenceCheck —
 * a centered overlay with title, body, and two CTAs.
 *
 * Crucially: this modal is gated to `trigger='step_submit'` score updates
 * ONLY. Session-resume score updates refresh the HUD bars but never
 * trigger this modal (per spec §11.1) — without that gate, a returning
 * user with a low pre-break score would see the modal the instant they
 * reopen a session before doing anything.
 */
import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useReadinessEvent } from '@/shared/api/hooks/useReadinessEvent';

interface ReadinessBreakModalProps {
  show: boolean;
  onTakeBreak: () => void;
  onKeepGoing: () => void;
  /** Telemetry context — passed through on shown / dismiss events. */
  sessionId?: string;
  score?: number;
  bars?: 1 | 2 | 3 | 4 | 5;
  algorithmVersion?: string;
}

export function ReadinessBreakModal({
  show,
  onTakeBreak,
  onKeepGoing,
  sessionId,
  score,
  bars,
  algorithmVersion,
}: ReadinessBreakModalProps) {
  const trackEvent = useReadinessEvent();
  const shownAtRef = useRef<number | null>(null);

  // Fire `readiness_modal_shown` once per show transition.
  useEffect(() => {
    if (show) {
      shownAtRef.current = Date.now();
      trackEvent('readiness_modal_shown', { sessionId, score, bars, algorithmVersion });
    } else {
      shownAtRef.current = null;
    }
  }, [show, sessionId, score, bars, algorithmVersion, trackEvent]);

  const secondsVisible = () => {
    const t = shownAtRef.current;
    return t ? Math.max(0, (Date.now() - t) / 1000) : 0;
  };

  const handleKeepGoing = () => {
    trackEvent('readiness_modal_dismissed_continue', {
      sessionId,
      score,
      bars,
      algorithmVersion,
      secondsVisible: secondsVisible(),
    });
    onKeepGoing();
  };

  const handleTakeBreak = () => {
    trackEvent('readiness_modal_dismissed_break', {
      sessionId,
      score,
      bars,
      algorithmVersion,
      secondsVisible: secondsVisible(),
    });
    onTakeBreak();
  };

  // Escape key dismisses with "Keep going" semantics (cooldown, no navigation).
  useEffect(() => {
    if (!show) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleKeepGoing();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Note: no AnimatePresence + exit animation here. AnimatePresence keeps the
  // wrapper mounted during the exit animation, and at z-50 fixed inset-0 it
  // sits on top of the underlying Submit/Next button. Even with no onClick on
  // the backdrop, the wrapper's default pointer-events: auto absorbs the
  // user's next intended click. Result: dismiss → click → swallowed → click
  // again → finally hits the button. Unmounting the modal synchronously on
  // dismiss avoids that whole class of bug. Enter animation still works
  // because motion.div animates on mount.
  if (!show) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="readiness-break-title"
    >
      {/* Backdrop — non-interactive. Previously had onClick=handleKeepGoing
          which silently swallowed the user's next intended click on the
          underlying Submit/Next button. Dismiss is now explicit: the two
          CTA buttons or the Escape key. */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0, 0, 0, 0.32)', pointerEvents: 'none' }}
        aria-hidden="true"
      />
      {/* Card */}
      <motion.div
        initial={{ scale: 0.96, y: 8 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-xl p-6 shadow-xl"
        style={{
          background: 'var(--f-bg-elevated, var(--f-bg, #fff))',
          color: 'var(--f-fg)',
          border: '1px solid var(--f-border, rgba(0,0,0,0.1))',
        }}
      >
        <h2 id="readiness-break-title" className="text-lg font-semibold mb-2">
          Time for a break?
        </h2>
        <p className="text-sm opacity-80 mb-5">
          You've been learning hard. Stepping away can help your brain consolidate what you've
          learnt.
        </p>
        <div className="flex flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={handleKeepGoing}
            className="rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2"
            style={{
              background: 'transparent',
              color: 'var(--f-fg)',
              border: '1px solid var(--f-border, rgba(0,0,0,0.15))',
            }}
          >
            Keep going
          </button>
          <button
            type="button"
            onClick={handleTakeBreak}
            className="rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2"
            style={{
              background: 'var(--f-accent, #6366f1)',
              color: '#fff',
              border: '1px solid var(--f-accent, #6366f1)',
            }}
            autoFocus
          >
            Take a break
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
